import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Alert, Button } from '@/components/ui';
import { useAnnouncer } from '@/lib/a11y';

import {
  COARSE_ACCURACY_M,
  GEOLOCATION_MESSAGES,
  GeolocationError,
  geolocationAvailable,
  locateDevice,
} from './geolocation';
import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  LOCATED_ZOOM,
  type LatLng,
  type MapPickerHandle,
  type MapProvider,
} from './mapProvider';
import { googleMapProvider } from './googleMapProvider';
import { hasGoogleMaps } from './googleMaps';
import { osmMapProvider } from './osmMapProvider';

/**
 * Google when the build has a key, OpenStreetMap when it does not.
 *
 * ONE LINE, as the seam promised. The fallback is not a courtesy: a developer
 * without a key, or a build where it was forgotten, gets a working map rather
 * than a blank panel and a console error — and the checkout still completes.
 */
const defaultMapProvider = hasGoogleMaps() ? googleMapProvider : osmMapProvider;

/**
 * The map's accessible name, and the instruction that comes with it.
 *
 * It says what to DO, not what it is: "map" is already carried by the role,
 * and somebody who cannot see the crosshair needs to be told that moving the
 * map is how the pin is placed.
 */
const MAP_LABEL = 'Map. Move it to put the marker on your door.';

export interface PickedLocation extends LatLng {
  /** How we got it. A rider should trust a fix and a pin differently. */
  source: 'device' | 'map';
  accuracyM: number | null;
}

export interface LocationPickerProps {
  value: PickedLocation | null;
  onChange: (location: PickedLocation) => void;
  /**
   * Move the map here.
   *
   * For a point the customer chose somewhere ELSE — a result from the search
   * box. The map cannot follow `value`, because `value` is mostly set BY the
   * map: recentring on it would fight every drag. So an outside choice says
   * so through its own prop, and changing it is what moves the map.
   *
   * This replaced an "approximate centre" taken from the PIN code's postal
   * centroid, which existed only because the flow asked for a PIN code first.
   */
  centreOn?: LatLng | null;
  /** Swapped for Google later; the component never learns which is in use. */
  provider?: MapProvider;
  /**
   * How much of the screen this is.
   *
   * `form` is the original: a labelled block inside a form, with its own
   * heading, its own "Use my location" button above the map and a status
   * line below. `screen` is the map AS the screen — no heading (the page
   * has one), the locate button floating over the map where the thumb is,
   * and whatever the parent passes as `overlay` (the search box) floating
   * over the top edge. The map and every rule about it are the same.
   */
  appearance?: 'form' | 'screen';
  /** Rendered over the top edge of the map in `screen` appearance. */
  overlay?: ReactNode;
  /**
   * The device said THIS is where the customer is. Reported separately from
   * `onChange` because a fix is not the answer, it is a starting point: the
   * customer drags from it, and the parent wants to know how far — a pin
   * three kilometres from where you are standing is usually a mistake.
   */
  onDeviceFix?: (coordinates: LatLng) => void;
}

/**
 * "Where is your door?" — asked once, answered two ways.
 *
 * A typed Indian address reliably gets a parcel to the street and no further.
 * "Near the temple, second lane" is a normal address line here, and the
 * difference between a pin and no pin is one delivery attempt versus a rider
 * phoning from the main road. That is why this is required rather than a nice
 * extra.
 *
 * TWO WAYS IN, because neither works alone. The device fix is one tap and
 * exact when it works; it is also the thing people decline, and it is useless
 * when they are ordering from the office for a delivery at home. The map
 * always works and is never wrong about what the customer meant, but it is
 * more effort. So: offer the fix, fall back to the map, and let the map
 * correct the fix.
 *
 * The map is the source of truth for the final value either way — a fix
 * recentres it, and whatever the customer leaves under the crosshair is what
 * gets saved.
 */
export function LocationPicker({
  value,
  onChange,
  centreOn = null,
  provider = defaultMapProvider,
  appearance = 'form',
  overlay,
  onDeviceFix,
}: LocationPickerProps) {
  const asScreen = appearance === 'screen';
  const { announce } = useAnnouncer();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<MapPickerHandle | null>(null);
  // The callback the map holds is captured once at creation, so it reads the
  // current onChange through a ref rather than pinning the first one.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [ready, setReady] = useState(false);
  const [mapFailed, setMapFailed] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    // A pin they already have beats a searched place, which beats the shop.
    // Only the first is an answer; the rest are somewhere to start.
    const start = value ?? centreOn ?? DEFAULT_CENTER;
    provider
      .create(container, {
        center: start,
        zoom: value ? LOCATED_ZOOM : DEFAULT_ZOOM,
        onCenterChanged: (centre) => {
          // Moving the map IS the answer, so every settle is a new value.
          // Marked as 'map' because the customer has now overruled whatever
          // the device said, and a corrected fix is a pin.
          onChangeRef.current({ ...centre, source: 'map', accuracyM: null });
        },
      })
      .then((handle) => {
        if (cancelled) {
          handle.destroy();
          return;
        }
        handleRef.current = handle;
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setMapFailed(true);
      });

    return () => {
      cancelled = true;
      handleRef.current?.destroy();
      handleRef.current = null;
    };
    // Created once. `value` is deliberately not a dependency: re-creating the
    // map on every pan would destroy it mid-gesture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  // The customer corrects their PIN code and the map follows — but only while
  // they have not set a pin themselves. Yanking the map out from under someone
  // who has just placed their marker would be worse than opening in the wrong
  // town in the first place.
  useEffect(() => {
    if (!ready || !centreOn) return;
    // LOCATED_ZOOM, not DEFAULT_ZOOM: a search result is a specific place, so
    // the map should arrive close enough to see the building rather than the
    // district. The customer then nudges the crosshair onto the door.
    handleRef.current?.setCenter(centreOn, LOCATED_ZOOM);
    // NOT gated on `value` being empty, which is what the old approximate
    // centre did. A customer who has already set a pin and then searches for
    // somewhere else must see the map move — otherwise the search silently
    // does nothing, which is exactly how it behaved before this changed.
  }, [ready, centreOn]);

  /**
   * Ask the browser where they are, once, as soon as the map is up.
   *
   * The customer is here to say where their parcel goes, and the device
   * usually knows. Asking straight away saves the common case a gesture —
   * and the prompt is the browser's own, so a refusal costs them one tap and
   * leaves the map on the shop's area to drag from.
   *
   * ONCE, and only with nothing set. It must not re-ask after they have
   * placed a pin, and it must not re-ask on every re-render: a permission
   * prompt that keeps reappearing is how a browser earns a permanent block.
   */
  const prompted = useRef(false);
  useEffect(() => {
    if (!ready || prompted.current || value !== null || !geolocationAvailable()) return;
    prompted.current = true;
    void locateMe();
    // Deliberately not depending on `value`: this fires once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // When the map IS the screen, put it where the screen is. The fulfilment
  // choice and the page heading sit above it, and on a phone they push the
  // crosshair below the fold — a map screen whose pin you have to scroll to
  // find is not a map screen. Once, when the map comes up; never on a later
  // render, which would yank the page out from under a drag.
  useEffect(() => {
    if (!ready || appearance !== 'screen') return;
    containerRef.current?.parentElement?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [ready, appearance]);

  async function locateMe() {
    setLocating(true);
    setLocateError(null);
    try {
      const located = await locateDevice();
      onDeviceFix?.(located.coordinates);
      handleRef.current?.setCenter(located.coordinates, LOCATED_ZOOM);
      onChange({
        ...located.coordinates,
        source: 'device',
        accuracyM: located.accuracyM,
      });

      if (located.accuracyM !== null && located.accuracyM > COARSE_ACCURACY_M) {
        // Not a failure, but not a doorstep either. Said in the announcement
        // rather than as a banner: a warning the customer cannot act on
        // beyond "move the map", which the text above already tells them,
        // is a box that trains people to dismiss boxes. The accuracy is
        // still shown beside the marker status.
        announce('Location found, but only roughly. Please check the marker.');
      } else {
        announce('Location found. Check the marker is on your door.');
      }
    } catch (error) {
      const reason = error instanceof GeolocationError ? error.reason : 'unavailable';
      setLocateError(GEOLOCATION_MESSAGES[reason]);
      announce(GEOLOCATION_MESSAGES[reason], 'assertive');
    } finally {
      setLocating(false);
    }
  }

  const locateButton = (
    <Button
      type="button"
      variant="secondary"
      onClick={() => void locateMe()}
      loading={locating}
      loadingText="Finding you…"
      disabled={!geolocationAvailable()}
    >
      Use my location
    </Button>
  );

  return (
    <div className="flex flex-col gap-2">
      {asScreen ? null : (
        <div>
          <p className="font-medium text-ink">Where is your door?</p>
          <p className="text-sm text-ink-muted">
            The rider uses this to find you. Use your location, then move the map if the marker is
            not quite right.
          </p>
        </div>
      )}

      {/* Beside the search box above, not under the map. These are the two
          ways to answer the same question — type where you are, or let the
          device say — and they belong together. Under the map it read as a
          correction to a pin the customer had already placed. */}
      {asScreen ? null : <div>{locateButton}</div>}

      {locateError ? (
        <Alert severity="warning" title="We could not use your location" live>
          <p>{locateError}</p>
        </Alert>
      ) : null}

      {mapFailed ? (
        <Alert severity="danger" title="The map could not load">
          <p>
            Check your connection and reload the page. You can still use your location if your
            browser allows it.
          </p>
        </Alert>
      ) : null}

      {/* `isolate` is load-bearing. The crosshair below needs z-800 to clear
          the map's own panes, but `relative` alone creates NO stacking
          context, so that 800 — and Leaflet's 700, and Google's controls at
          1000 — resolve against the ROOT, where they beat the sticky action
          bar at z-30 and paint the map over the total and the pay button.
          Isolating here keeps those numbers a private matter of the map. */}
      <div
        data-testid="location-map-card"
        className="relative isolate overflow-hidden rounded-card border border-line"
      >
        {/* Taller than it looks like it needs to be, on purpose. The customer
            is picking a BUILDING, and at that zoom a short map shows a couple
            of streets and no landmarks to recognise — so they end up dragging
            blind. Taller on wider screens, where there is room for it without
            pushing the rest of the form off the page. */}
        {/* The name and the role live HERE, not in the provider that draws
            the tiles. Creating a map is asynchronous, so a remount leaves two
            `create` calls racing over this one node and the loser's teardown
            used to strip the winner's attributes — see the note in
            `mapProvider`. React owns them, so nothing late can reach them.

            Only once there IS a map: `role="application"` on an empty grey
            box would announce an interactive widget that cannot be used. */}
        <div
          ref={containerRef}
          className={
            asScreen
              ? // As tall as the phone allows with the action bar still on
                // screen. A map that IS the screen should not look like a
                // widget with a lot of page around it.
                'h-[min(60dvh,34rem)] w-full bg-surface-sunken'
              : 'h-96 w-full bg-surface-sunken sm:h-[32rem]'
          }
          {...(ready ? { role: 'application', 'aria-label': MAP_LABEL } : { 'aria-hidden': true })}
        />

        {/* The crosshair. Fixed dead centre, on top of the map, and
            pointer-events-none so it never eats the pan gesture underneath. */}
        {ready ? (
          /* z-800 is not decoration. Leaflet's own panes climb to 700 — tiles
             200, markers 600, popups 700 — and they are children of the map
             container, so an overlay left at `z-index: auto` loses to every
             one of them and the pin is painted UNDER the map. Above the panes,
             below the controls at 1000, so +/- stay on top. */
          <div
            aria-hidden="true"
            data-testid="location-crosshair"
            className="pointer-events-none absolute inset-0 z-[800] grid place-items-center"
          >
            <svg viewBox="0 0 24 32" className="h-9 w-9 drop-shadow" fill="none">
              <path
                d="M12 31C12 31 21 19.5 21 12A9 9 0 1 0 3 12c0 7.5 9 19 9 19Z"
                className="fill-danger"
              />
              <circle cx="12" cy="12" r="3.25" className="fill-white" />
            </svg>
          </div>
        ) : (
          <div className="absolute inset-0 grid place-items-center text-sm text-ink-muted">
            Loading the map…
          </div>
        )}

        {/* Over the map, not above it: this is the screen appearance's whole
            point. z-[1100] clears Google's own controls at 1000, so the
            search list drops down over the map rather than under its +/-.
            `pointer-events-none` on the layer and `auto` on the children, so
            the map still pans everywhere the floating things are not. */}
        {asScreen && overlay ? (
          <div className="pointer-events-none absolute inset-x-3 top-3 z-[1100]">
            <div className="pointer-events-auto">{overlay}</div>
          </div>
        ) : null}
        {asScreen && ready ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-3 z-[1050] grid place-items-center">
            <div className="pointer-events-auto rounded-full bg-surface shadow-lg">
              {locateButton}
            </div>
          </div>
        ) : null}
      </div>

      <div className={asScreen ? 'hidden' : 'flex flex-wrap items-center gap-2'}>
        {value ? (
          <p className="text-sm text-ink-subtle">
            Marker set
            {value.source === 'device' && value.accuracyM !== null
              ? ` (±${value.accuracyM} m)`
              : ''}
          </p>
        ) : (
          <p className="text-sm text-ink-subtle">Move the map to set the marker.</p>
        )}
      </div>
    </div>
  );
}
